import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { Variant } from "@/lib/landing/cinematicTransitions";

export interface CinematicNavOptions {
  variant?: Variant;
  originPoint?: { x: number; y: number };
  preload?: () => Promise<unknown>;
}

/**
 * Every navigation is plain and instant.
 *
 * Landing routes have been plain since the 2026-05-18 user decision
 * (shipped 065c935b). Non-landing routes used to hand off to
 * `ctx.startTransition()`, but the same 2026-05-18 dead-code pass left
 * CinematicTransitionLayer and CinematicRouteFrame as pass-throughs, so
 * nothing consumed that state or ever called navigate — a non-landing
 * CinematicLink click preventDefault()ed and then went nowhere (the
 * signed-in "Dashboard" nav CTA was a dead click, found 2026-07-17).
 * Plain navigate for every route is the fix; `options.preload` still
 * fires so destination chunks warm up.
 */
export function useCinematicNavigate(): (to: string, options?: CinematicNavOptions) => void {
  const navigate = useNavigate();

  return useCallback(
    (to: string, options?: CinematicNavOptions) => {
      options?.preload?.();
      navigate(to);
    },
    [navigate]
  );
}
